import { getRouterInstance } from './router-instance';

/**
 * Legacy URL scheme is client-scoped: every MFE lives under
 * `/{clientId}/app-container/{appId}/...`. When the active client changes we
 * must rewrite the leading `clientId` segment (preserving the rest of the path
 * and the query string, incl. `?popup=`) and navigate there — the React
 * equivalent of the Angular header's `URLClientChange` navigation.
 */
const CLIENT_SCOPED_RE = /^\/[^/]+\/app-container(?<rest>\/.*)?$/;

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapse a nested client-scoped path back to a single scope. A buggy MFE
 * relative navigation can append its own `{clientId}/app-container/{appId}`
 * onto the current (already client-scoped) URL, so the path grows without
 * bound and the two clients alternate — the Bug #2 loop:
 *
 *   /A/app-container/mad/B/app-container/mad/A/app-container/mad/entities
 *     → /A/app-container/mad/entities
 *
 * We keep the OUTERMOST `{clientId}/app-container/{appId}` (what the router
 * parsed as the route params) and drop every subsequent
 * `{guid}/app-container/{appId2}` group, preserving the innermost real tail.
 * The GUID gate on each stripped group avoids collapsing a legitimate MFE deep
 * route that merely contains an `app-container` segment.
 */
export function collapseClientScopedPath(pathname: string): string {
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length < 3 || segs[1] !== 'app-container') return pathname;

  const [clientId, , appId] = segs;
  let cursor = 3;
  let tailStart = 3;
  while (
    cursor + 2 < segs.length &&
    GUID_RE.test(segs[cursor] ?? '') &&
    segs[cursor + 1] === 'app-container'
  ) {
    cursor += 3;
    tailStart = cursor;
  }

  if (tailStart === 3) return pathname;

  const tail = segs.slice(tailStart);
  return `/${[clientId, 'app-container', appId, ...tail].join('/')}`;
}

/**
 * Extract the leading clientId (first path segment) when it is a GUID, else
 * `null`. Used by the browser-back client-switch guard to detect a POP whose
 * target URL crosses into a different client scope.
 */
export function clientIdFromPathname(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return GUID_RE.test(first) ? first : null;
}

/**
 * Parse the active app out of a client-scoped pathname
 * (`/{clientId}/app-container/...`) into the mount `appId` and the legacy screen
 * path used by the role/tile access gate. Mirrors the Angular shell's
 * `getCurrentURLSegement` (`side-navigation.component.ts`):
 *
 *   /{c}/app-container/workspace/apps/tfer → { appId: 'tfer', screenPath: 'workspace/apps/tfer' }
 *   /{c}/app-container/admin/apps/rum      → { appId: 'rum',  screenPath: 'admin/apps/rum' }
 *   /{c}/app-container/mad                  → { appId: 'mad',  screenPath: 'mad' }
 *   /{c}/app-container/home                 → { appId: 'home', screenPath: 'home' }
 *
 * The `workspace/apps` and `admin/apps` scope prefixes are kept in the screen
 * path (that is how the tile feed is keyed); the bare form collapses to the
 * single segment. A naive `app-container/([^/]+)` capture instead returns the
 * SCOPE segment (`workspace`/`admin`) for the two `.../apps/...` forms, which is
 * why the portal refresh tile-gate was checking `workspace/apps/workspace` and
 * bouncing every workspace/admin app to home. Query string and any legacy
 * `(popup:…)` suffix are ignored.
 */
export function parseClientScopedApp(pathname: string): { appId: string; screenPath: string } {
  const match = /\/app-container\/([^?#]+)/.exec(pathname);
  const segs = (match?.[1] ?? '')
    .split('/')
    .map((seg) => seg.split('(')[0])
    .filter(Boolean);
  if ((segs[0] === 'workspace' || segs[0] === 'admin') && segs[1] === 'apps' && segs[2]) {
    return { appId: segs[2], screenPath: `${segs[0]}/apps/${segs[2]}` };
  }
  return { appId: segs[0] ?? '', screenPath: segs[0] ?? '' };
}

/**
 * Build the client-scoped path for `clientId` from the current pathname.
 * If the current path is already client-scoped (`/{x}/app-container/...`) the
 * `app-container/...` tail is preserved; otherwise we land on the client's
 * home MFE (legacy parity: switching client outside an MFE returns home).
 *
 * The pathname is collapsed first so switching client on an already-nested URL
 * (Bug #2) yields a single clean scope instead of inheriting the nested tail.
 */
export function clientScopedPath(clientId: string, currentPathname: string): string {
  const collapsed = collapseClientScopedPath(currentPathname);
  const rest = CLIENT_SCOPED_RE.exec(collapsed)?.groups?.rest ?? '/home';
  return `/${clientId}/app-container${rest}`;
}

/**
 * Client-access gate. When the clientId in the URL is NOT one of the user's
 * accessible clients (the RUM client list is the source of truth), the user has
 * no access to that client at all — legacy bounced them to their first available
 * client's home. Returns the home href of the fallback client (the currently
 * selected one if still valid, else the first available), or `null` when the
 * URL client is accessible / nothing is loaded yet (so callers do not act
 * before the client list resolves).
 */
export function clientAccessRedirect(
  urlClientId: string,
  available: readonly { id: string }[],
  currentId: string | null,
): string | null {
  if (!urlClientId || available.length === 0) return null;
  if (available.some((c) => c.id === urlClientId)) return null;
  const target = available.find((c) => c.id === currentId) ?? available[0];
  return target ? `/${target.id}/app-container/home` : null;
}

/**
 * Navigate to the current view under a different client. Uses the router's OWN
 * history (see `router-instance.ts`) so TanStack reliably re-matches the route.
 * No-op before the router registers.
 */
export function navigateToClientScope(clientId: string): void {
  const router = getRouterInstance();
  if (!router) return;
  const { pathname, searchStr } = router.state.location;
  const href = clientScopedPath(clientId, pathname) + (searchStr ?? '');
  router.history.push(href);
}
